import { AssessmentInfo } from "@verity/api-client/types";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { FC, ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { createPortal } from "react-dom";
import { EditQuizModalSkeleton } from "..";
import { api as defaultApi, VerityApi } from "../api/fetch";
import { CoreToastContainer, StepConfig } from "../core";
import ThemeWrapper from "../theme";
import { EditQuizToastContext } from "./context/EditQuizContext";
import { EditQuizPage, EditQuizPageProps, EditQuizRef } from "./editQuiz";
import { UnsavedAssessmentInfo } from "./editQuizModalWrapper";
import useEditQuizWrapper, { UseEditQuizWrapperProps, UseEditQuizWrapperReturn } from "./hooks/useEditQuizWrapper";
import { EditQuizAssessmentUUids, PendingLiveSession } from "./live/liveHelper";
import { EditQuizStep, RenderableProctoringKey } from "./proctoring-options/option-text/ProctoringOptionText";

export type EditQuizWrapperProps = {
	quizName?: string;
	api?: VerityApi;
	quizInfo: UnsavedAssessmentInfo;
	portalElement?: HTMLElement;
	onClose: () => void;
} & Pick<
	EditQuizPageProps,
	| "onSaveProfileChange"
	| "step"
	| "setStep"
	| "onBackEnabledChange"
	| "onNextEnabledChange"
	| "onSaveEnabledChange"
	| "setIsFinalPage"
	| "setIsFirstPage"
	| "onSaveOptions"
	| "uploadedExemptedStudentsMap"
	| "setUploadedExemptedStudentsMap"
	| "uploadedProfilesMap"
	| "setUploadedProfilesMap"
	| "logger"
	| "quizType"
	| "forceStartOnFirstPage"
	| "onStartSaveOptions"
	| "entryPoint"
> &
	Pick<UseEditQuizWrapperProps, "onInitialOptions" | "onEnabledChange" | "forceThirdParty">;

type EditQuizData = Pick<
	UseEditQuizWrapperReturn["state"],
	"proctoringAssessmentConfiguration" | "proctoringOption"
> & {
	activeProfile: ProctoringOptionProfileV2 | undefined;
	numExempted: number;
	pendingLiveSessions: PendingLiveSession[];
};

export type EditQuizWrapperRef = {
	saveButtonHandler: (password?: string, additionalQuizInfo?: Partial<AssessmentInfo>) => Promise<boolean>;
	closeButtonHandler: () => void;
	saveAsProfile: () => void;
	setEnabled: (enabled: boolean) => void;
	nextButtonHandler: () => void;
	backButtonHandler: () => void;
	goToPage: (pageName: EditQuizStep, proctoringOptionToFocus?: RenderableProctoringKey) => void;
	getStepKeyByIndex: (index: number) => EditQuizStep;
	getStepConfig: () => StepConfig<EditQuizStep>[];
	/**For confirmation step in the verity assessment workflow */
	getQuizData: () => EditQuizData;
};

export type EditQuizSwitchComponent = FC<{
	enabled: boolean;
	setEnabled: (_: boolean) => void;
}>;

export const EditQuizWrapper = forwardRef(function EditQuizWrapper(
	{
		api = defaultApi,
		quizInfo,
		portalElement,
		onStartSaveOptions,
		onSaveOptions,
		onInitialOptions,
		onEnabledChange,
		onBackEnabledChange,
		onNextEnabledChange,
		onSaveEnabledChange,
		onClose,
		setIsFinalPage,
		forceThirdParty = false,
		uploadedExemptedStudentsMap,
		setUploadedExemptedStudentsMap,
		uploadedProfilesMap,
		setUploadedProfilesMap,
		forceStartOnFirstPage,
		logger,
		step,
		setStep,
		onSaveProfileChange,
		quizName,
		quizType,
		setIsFirstPage,
		entryPoint,
	}: EditQuizWrapperProps,
	ref: ForwardedRef<EditQuizWrapperRef>,
) {
	const currFakeLiveSessionId = useRef(0);
	const assessmentUuids = useRef<EditQuizAssessmentUUids | undefined>();

	const editQuizPageRef = useRef<EditQuizRef>(null);

	const { state, action } = useEditQuizWrapper({
		api,
		quizInfo,
		forceThirdParty,
		onInitialOptions,
		logger,
		onEnabledChange,
	});

	const {
		currentProfileId,
		defaultProctoringOption,
		enforcedProfilesOnly,
		initialProctoringAssessmentConfiguration,
		proctoringAssessmentConfiguration,
		initialProctoringOption,
		proctoringOption,
		pendingLiveSessions,
		pendingLiveSessionIds,
		existingLiveSessions,
		pendingLiveSessionsArray,
	} = state;

	const {
		refreshSettings,
		setProctoringAssessmentConfiguration,
		setProctoringOption,
		setPendingLiveSessions,
		setPendingLiveSessionIds,
		setCurrentProfileId,
		setExistingLiveSessions,
		handleEnabledChange,
	} = action;

	const saveButtonHandler: EditQuizWrapperRef["saveButtonHandler"] = async (password, additionalQuizInfo) =>
		(await editQuizPageRef.current?.saveButtonHandler(password, additionalQuizInfo)) ?? false;

	const closeButtonHandler: EditQuizWrapperRef["closeButtonHandler"] = () => {
		void editQuizPageRef.current?.closeButtonHandler();
	};

	const saveAsProfile: EditQuizWrapperRef["saveAsProfile"] = () => {
		editQuizPageRef.current?.saveAsProfileButtonHandler();
	};

	const nextButtonHandler: EditQuizWrapperRef["nextButtonHandler"] = () => {
		editQuizPageRef.current?.nextButtonHandler();
	};

	const backButtonHandler: EditQuizWrapperRef["backButtonHandler"] = () => {
		editQuizPageRef.current?.backButtonHandler();
	};

	const goToPage: EditQuizWrapperRef["goToPage"] = (
		pageName: EditQuizStep,
		proctoringOptionToFocus?: RenderableProctoringKey,
	) => {
		editQuizPageRef.current?.goToPage(pageName, proctoringOptionToFocus);
	};

	const getStepKeyByIndex: EditQuizWrapperRef["getStepKeyByIndex"] = (index: number) => {
		return editQuizPageRef.current?.getStepKeyByIndex(index) ?? "confirmation";
	};

	const getStepConfig: EditQuizWrapperRef["getStepConfig"] = () => {
		return editQuizPageRef.current?.getStepConfig() ?? [];
	};

	const getQuizData: EditQuizWrapperRef["getQuizData"] = useCallback(() => {
		const activeProfile = editQuizPageRef.current?.getActiveProfile();
		const numExempted = editQuizPageRef.current?.getNumExempted() ?? 0;

		return {
			proctoringOption,
			proctoringAssessmentConfiguration,
			pendingLiveSessions: pendingLiveSessionsArray,
			activeProfile,
			numExempted,
		};
	}, [proctoringOption, proctoringAssessmentConfiguration, pendingLiveSessionsArray]);

	useImperativeHandle(ref, () => ({
		saveButtonHandler,
		closeButtonHandler,
		setEnabled: handleEnabledChange,
		saveAsProfile,
		nextButtonHandler,
		backButtonHandler,
		goToPage,
		getStepConfig,
		getStepKeyByIndex,
		getQuizData,
	}));

	useEffect(() => {
		void refreshSettings();
	}, []);

	let optionsPage;
	optionsPage = (
		<ThemeWrapper scoped={false}>
			<EditQuizPage
				quizName={quizName}
				ref={editQuizPageRef}
				api={api}
				quizInfo={quizInfo}
				initialProctoringAssessmentConfiguration={initialProctoringAssessmentConfiguration}
				proctoringAssessmentConfiguration={proctoringAssessmentConfiguration}
				setProctoringAssessmentConfiguration={setProctoringAssessmentConfiguration}
				initialProctoringOption={initialProctoringOption}
				proctoringOption={proctoringOption}
				setProctoringOption={setProctoringOption}
				existingLiveSessions={existingLiveSessions}
				setExistingLiveSessions={setExistingLiveSessions}
				pendingLiveSessions={pendingLiveSessions}
				setPendingLiveSessions={setPendingLiveSessions}
				pendingLiveSessionIds={pendingLiveSessionIds}
				setPendingLiveSessionIds={setPendingLiveSessionIds}
				currFakeLiveSessionId={currFakeLiveSessionId}
				assessmentUuids={assessmentUuids}
				currentProfileId={currentProfileId}
				setCurrentProfileId={setCurrentProfileId}
				enforcedProfilesOnly={enforcedProfilesOnly}
				onBackEnabledChange={onBackEnabledChange}
				onNextEnabledChange={onNextEnabledChange}
				onSaveEnabledChange={onSaveEnabledChange}
				onClose={onClose}
				setIsFinalPage={setIsFinalPage}
				forceStartOnFirstPage={forceStartOnFirstPage}
				onStartSaveOptions={(confirmedOptions) => {
					onStartSaveOptions?.();
					refreshSettings(confirmedOptions);
				}}
				onSaveOptions={onSaveOptions ?? (() => null)}
				uploadedExemptedStudentsMap={uploadedExemptedStudentsMap}
				setUploadedExemptedStudentsMap={setUploadedExemptedStudentsMap}
				uploadedProfilesMap={uploadedProfilesMap}
				setUploadedProfilesMap={setUploadedProfilesMap}
				logger={logger}
				defaultProctoringOption={defaultProctoringOption}
				step={step}
				setStep={setStep}
				quizType={quizType}
				entryPoint={entryPoint}
				onSaveProfileChange={onSaveProfileChange}
				setIsFirstPage={setIsFirstPage}
			/>
		</ThemeWrapper>
	);
	if (portalElement) {
		optionsPage = createPortal(optionsPage, portalElement);
	}

	const toastContainerId = `toast-${quizInfo.quizId}`;

	return (
		<EditQuizToastContext.Provider value={toastContainerId}>
			{!proctoringAssessmentConfiguration && <EditQuizModalSkeleton />}
			{optionsPage}
			<CoreToastContainer containerId={toastContainerId} enableMultiContainer />
		</EditQuizToastContext.Provider>
	);
});
